import { useMemo } from "react";
import { Group } from "@visx/group";
import { LinePath } from "@visx/shape";
import { scaleLinear } from "@visx/scale";
import { AxisLeft, AxisBottom } from "@visx/axis";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { curveMonotoneX } from "@visx/curve";
import { getCategoryColor } from "../../utils/categoryColors";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import ResponsiveWrapper from "../ResponsiveWrapper/ResponsiveWrapper";
import { formatCurrency } from "../../utils/format";
import { useScreenHeight, getChartHeight } from "../../hooks/useScreenHeight";

const margin = { top: 20, right: 20, bottom: 25, left: 55 };

const LineChart = ({
	data,
	translate,
	averages,
	displayMetrics,
	yearlyGrowthRates,
}) => {
	const { locale } = useUserPreferences();
	const currentYear = new Date().getFullYear();
	const screenHeight = useScreenHeight();
	const chartHeight = getChartHeight(screenHeight);

	// Extract years and categories
	const years = data.map(d => d.year);
	const categoryKeys = Object.keys(data[0]?.categories || {});

	// Prepare data with projections
	const processedData = useMemo(() => {
		if (!data || data.length === 0) return [];

		return data.map(yearData => {
			const isCurrentYear = yearData.year === currentYear;
			const yearAverages = averages?.[yearData.year];
			const yearDisplayMetrics = displayMetrics?.[yearData.year];

			if (isCurrentYear && yearAverages && yearDisplayMetrics) {
				const categoryKeys = Object.keys(yearData.categories || {});
				const projectedCategories = {};

				categoryKeys.forEach(category => {
					const monthlyAverage = yearAverages.categories?.[category] || 0;
					projectedCategories[category] = monthlyAverage * 12;
				});

				return {
					...yearData,
					categories: projectedCategories,
					projectedCategories,
					isCurrentYear: true,
					monthsForAverage: yearDisplayMetrics.monthsForAverage || 1,
				};
			}

			return {
				...yearData,
				projectedCategories: yearData.categories,
				isCurrentYear: false,
			};
		});
	}, [data, averages, displayMetrics, currentYear]);

	// Flatten all values for scale domain (including projections)
	const allValues = processedData.flatMap(d => [
		...Object.values(d.categories || {}),
		...Object.values(d.projectedCategories || {}),
	]);
	const maxValue = Math.max(...allValues, 0);

	// Tooltip setup
	const {
		tooltipData,
		tooltipLeft,
		tooltipTop,
		tooltipOpen,
		showTooltip,
		hideTooltip,
	} = useTooltip();
	const { containerRef, TooltipInPortal } = useTooltipInPortal({
		scroll: true,
	});

	const renderChart = ({ width, height }) => {
		if (width === 0 || height === 0 || processedData.length === 0) return null;

		const xMax = width - margin.left - margin.right;
		const yMax = height - margin.top - margin.bottom;

		const xScale = scaleLinear({
			domain: [Math.min(...years), Math.max(...years)],
			range: [0, xMax],
		});

		const yScale = scaleLinear({
			domain: [0, maxValue * 1.1],
			range: [yMax, 0],
			nice: true,
		});

		// Helper function to find closest data point
		const findClosestDataPoint = mouseX => {
			const xPos = mouseX - margin.left;
			let closestDistance = Infinity;
			let closestIndex = 0;

			processedData.forEach((d, index) => {
				const distance = Math.abs(xScale(d.year) - xPos);
				if (distance < closestDistance) {
					closestDistance = distance;
					closestIndex = index;
				}
			});

			return closestIndex;
		};

		return (
			<svg width={width} height={height}>
				<Group top={margin.top} left={margin.left}>
					<AxisLeft
						scale={yScale}
						tickLabelProps={() => ({
							fill: "var(--text-color)",
							fontSize: 11,
							textAnchor: "end",
						})}
						tickLineProps={{
							stroke: "var(--text-color)",
							strokeWidth: 1,
						}}
					/>
					<AxisBottom
						scale={xScale}
						top={yMax}
						tickValues={years}
						tickFormat={value => String(Math.round(value))}
						tickLabelProps={() => ({
							fill: "var(--text-color)",
							fontSize: 11,
							textAnchor: "middle",
						})}
						tickLineProps={{
							stroke: "var(--text-color)",
							strokeWidth: 1,
						}}
					/>

					{categoryKeys.map(category => {
						// Split the data into two parts: before the current year and current year
						const dataBeforeCurrentYear = processedData.filter(
							d => !d.isCurrentYear
						);
						const currentYearData = processedData.filter(d => d.isCurrentYear);

						// Create the data for the continuous line (all years except the current year)
						const continuousData = dataBeforeCurrentYear;

						// Create the data for the dotted line (last year + current year)
						const projectionData = [];
						if (
							dataBeforeCurrentYear.length > 0 &&
							currentYearData.length > 0
						) {
							// Add the last point of the continuous line
							const lastPoint =
								dataBeforeCurrentYear[dataBeforeCurrentYear.length - 1];
							projectionData.push(lastPoint);
							// Add the current year point
							projectionData.push(currentYearData[0]);
						}

						return (
							<g key={category}>
								{/* Solid line for actual data (except for current year) */}
								{continuousData.length > 0 && (
									<LinePath
										data={continuousData}
										x={d => xScale(d.year)}
										y={d => yScale(d.categories[category] || 0)}
										stroke={getCategoryColor(category)}
										strokeWidth={3}
										opacity={0.9}
										curve={curveMonotoneX}
									/>
								)}

								{/* Dashed line for projections (only for current year) */}
								{projectionData.length > 1 && (
									<LinePath
										data={projectionData}
										x={d => xScale(d.year)}
										y={d => yScale(d.categories[category] || 0)}
										stroke={getCategoryColor(category)}
										strokeWidth={2}
										opacity={0.7}
										strokeDasharray="5,5"
										curve={curveMonotoneX}
									/>
								)}

								{/* Circles on data points */}
								{processedData.map((d, index) => (
									<circle
										key={`${category}-${index}`}
										cx={xScale(d.year)}
										cy={yScale(d.categories[category] || 0)}
										r={d.isCurrentYear ? 4 : 4}
										fill={getCategoryColor(category)}
										stroke="white"
										strokeWidth={d.isCurrentYear ? 2 : 2}
										opacity={1}
									/>
								))}
							</g>
						);
					})}

					{/* Invisible overlay for better mouse interaction */}
					<rect
						width={xMax}
						height={yMax}
						fill="transparent"
						onMouseMove={event => {
							const evt = event.nativeEvent;
							const closestIndex = findClosestDataPoint(evt.offsetX);
							const closestData = processedData[closestIndex];

							// Find the category with the highest value for this data point
							const categoryValues = categoryKeys.map(cat => ({
								category: cat,
								actualValue: closestData.categories[cat] || 0,
								projectedValue: closestData.projectedCategories[cat] || 0,
								isCurrentYear: closestData.isCurrentYear,
								growthRate:
									yearlyGrowthRates?.[closestData.year]?.[cat] ?? null,
							}));

							// Sort by projected value to show the most relevant category
							categoryValues.sort(
								(a, b) => b.projectedValue - a.projectedValue
							);
							const primaryCategory = categoryValues[0];

							showTooltip({
								tooltipLeft: evt.offsetX,
								tooltipTop: evt.offsetY,
								tooltipData: {
									category: primaryCategory.category,
									year: closestData.year,
									actualValue: closestData.isCurrentYear
										? 0
										: primaryCategory.actualValue,
									projectedValue: closestData.isCurrentYear
										? primaryCategory.projectedValue
										: primaryCategory.actualValue,
									isCurrentYear: primaryCategory.isCurrentYear,
									monthsForAverage: closestData.monthsForAverage,
									allCategories: categoryValues.filter(
										c => c.projectedValue > 0
									),
								},
							});
						}}
						onMouseLeave={hideTooltip}
					/>

					{/* Legend for current year */}
					{processedData.some(d => d.isCurrentYear) && (
						<g>
							<line
								x1={xMax - 25}
								y1={5}
								x2={xMax}
								y2={5}
								stroke="var(--text-color)"
								strokeWidth={3}
							/>
							<text
								x={xMax - 30}
								y={8}
								fontSize={10}
								fill="var(--text-color)"
								opacity={0.8}
								textAnchor="end"
							>
								{translate("analysis.legend.actual")}
							</text>

							<line
								x1={xMax - 25}
								y1={20}
								x2={xMax}
								y2={20}
								stroke="var(--text-color)"
								strokeWidth={2}
								strokeDasharray="5,5"
								opacity={0.6}
							/>
							<text
								x={xMax - 30}
								y={23}
								fontSize={10}
								fill="var(--text-color)"
								opacity={0.8}
								textAnchor="end"
							>
								{translate("analysis.legend.projected")}
							</text>
						</g>
					)}
				</Group>
			</svg>
		);
	};

	return (
		<div
			className="chart-box"
			style={{ height: `${chartHeight}px` }}
			ref={containerRef}
		>
			<ResponsiveWrapper fallbackHeight={chartHeight}>
				{renderChart}
			</ResponsiveWrapper>
			{tooltipOpen && tooltipData && (
				<TooltipInPortal
					top={tooltipTop}
					left={tooltipLeft}
					className="chart-tooltip"
				>
					<div className="tooltip-header">
						{tooltipData.year}
						{tooltipData.isCurrentYear && (
							<span className="tooltip-months">
								{" "}
								(
								{translate("analysis.tooltip.basedOnMonths", {
									count: tooltipData.monthsForAverage,
								})}
								)
							</span>
						)}
					</div>

					<div className="tooltip-list">
						{tooltipData.allCategories.map(cat => (
							<div key={cat.category} className="tooltip-item">
								<span
									className="tooltip-category"
									style={{ color: getCategoryColor(cat.category) }}
								>
									{translate("category." + cat.category)}
								</span>
								{cat.isCurrentYear ? (
									<span className="tooltip-projection">
										{formatCurrency(cat.projectedValue, locale)}
									</span>
								) : (
									<span>{formatCurrency(cat.actualValue, locale)}</span>
								)}
								{cat.growthRate !== null && cat.growthRate !== undefined && (
									<span
										className={`tooltip-growth ${
											cat.growthRate >= 0 ? "positive" : "negative"
										}`}
									>
										{`${cat.growthRate >= 0 ? "+" : ""}${cat.growthRate}%`}
									</span>
								)}
							</div>
						))}
					</div>
				</TooltipInPortal>
			)}
		</div>
	);
};

export default LineChart;
